import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { DashboardSession } from '../../types/dashboard';
import { SessionRow } from './SessionRow';
import { EmptyState } from '../ui/EmptyState';
import { formatCurrency } from '../../utils/currency';
import {
  browserTimeZoneAbbr,
  formatLocalDay,
  localDateKey,
  localTodayKey,
  parseDateTime,
} from '../../utils/sessionTime';

interface TodaySessionsProps {
  sessions: DashboardSession[];
  periodLabel?: string;
  loading?: boolean;
  empty?: boolean;
}

export function TodaySessions({ sessions, periodLabel, loading = false, empty = false }: TodaySessionsProps) {
  const navigate = useNavigate();
  const totalFee = sessions.reduce((sum, s) => sum + s.fee, 0);
  const totalMinutes = sessions.reduce((sum, s) => sum + s.durationMinutes, 0);
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const durationStr = `${hours}h ${mins}m`;

  // Group sessions by their browser-local day. Stored times are naive-UTC,
  // so the local date can differ from the stored date — always key off the
  // converted local day so each session lands under the day the viewer sees.
  const groupedSessions = useMemo(() => {
    const todayKey = localTodayKey();
    const groups: Record<string, DashboardSession[]> = {};
    sessions.forEach(session => {
      const key = localDateKey(session.date, session.startTime);
      (groups[key] ??= []).push(session);
    });
    const instant = (s: DashboardSession) =>
      parseDateTime(s.date, s.startTime)?.getTime() ?? 0;
    return Object.keys(groups).sort().map(key => {
      const groupSessions = groups[key].sort((a, b) => instant(a) - instant(b));
      const first = groupSessions[0];
      return {
        key,
        isToday: key === todayKey,
        dayLabel: formatLocalDay(first.date, first.startTime),
        tzAbbr: browserTimeZoneAbbr(first.date, first.startTime),
        sessions: groupSessions,
      };
    });
  }, [sessions]);

  // Title and empty message derived from periodLabel
  const title = periodLabel ? `${periodLabel}'s sessions` : "Today's sessions";
  const emptyMessage = periodLabel
    ? `No sessions scheduled ${periodLabel === 'Today' ? 'today' : periodLabel.toLowerCase()}.`
    : "No sessions scheduled today.";

  // Show skeleton when loading and no sessions
  if (loading && sessions.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 hair-b">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          <p className="text-[12px] text-ink-4 mt-0.5">Loading...</p>
        </div>

        {/* Skeleton rows */}
        <div className="divide-hair">
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-5 py-4 grid grid-cols-12 gap-3 items-center">
              <div className="col-span-2">
                <div className="bg-canvas-2 h-6 w-16 rounded animate-pulse" />
                <div className="bg-canvas-2 h-3 w-24 mt-2 rounded animate-pulse" />
              </div>
              <div className="col-span-4 flex items-center gap-2.5">
                <div className="bg-canvas-2 w-7 h-7 rounded animate-pulse" />
                <div className="min-w-0 flex-1">
                  <div className="bg-canvas-2 h-4 w-32 rounded animate-pulse mb-1.5" />
                  <div className="bg-canvas-2 h-3 w-24 rounded animate-pulse" />
                </div>
              </div>
              <div className="col-span-3">
                <div className="bg-canvas-2 h-4 w-28 rounded animate-pulse mb-1" />
                <div className="bg-canvas-2 h-3 w-20 rounded animate-pulse" />
              </div>
              <div className="col-span-2">
                <div className="bg-canvas-2 h-6 w-24 rounded-full animate-pulse" />
              </div>
              <div className="col-span-1">
                <div className="bg-canvas-2 h-6 w-12 rounded animate-pulse ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state when no sessions (use either empty prop or sessions.length check)
  if (empty || sessions.length === 0) {
    return (
      <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
        <div className="px-5 py-4 hair-b">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          <p className="text-[12px] text-ink-4 mt-0.5">{emptyMessage}</p>
        </div>
        <EmptyState
          heading={emptyMessage}
          description="Sessions will appear here once bookings are confirmed against available time slots."
        />
      </div>
    );
  }

  return (
    <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 hair-b">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        <p className="text-[12px] text-ink-4 mt-0.5">
          {sessions.length} sessions · {durationStr} · ${formatCurrency(totalFee)} booked
        </p>
      </div>

      {/* Session rows — always grouped by browser-local day so the day and
          timezone are explicit. */}
      <div className="divide-hair">
        {groupedSessions.map((group) => (
          <div key={group.key}>
            <div
              className={`px-5 py-2 text-[12px] font-semibold flex items-center justify-between ${
                group.isToday ? 'bg-tide-ss/30 text-ink-5' : 'text-ink-4 bg-paper'
              }`}
            >
              <span>
                {group.isToday && <span className="mr-2">Today</span>}
                {group.dayLabel}
              </span>
              {group.tzAbbr && (
                <span className="text-[11px] font-medium text-ink-4">{group.tzAbbr}</span>
              )}
            </div>
            {group.sessions.map((session) => (
              <SessionRow key={session.id} session={session} onClick={() => navigate(`/sessions/${session.id}`)} />
            ))}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 hair-t bg-wash flex items-center justify-between text-[12px] text-ink-3">
        <span>
          {sessions.length} sessions · {durationStr} of practice time
        </span>
        <span className={`font-display text-[14px] num ${sessions.some(s => s.feeIsEstimate) ? 'text-ink-3' : ''}`}>
          Total {sessions.some(s => s.feeIsEstimate) ? '~' : ''}${formatCurrency(totalFee)}
        </span>
      </div>
    </div>
  );
}
